<script setup>
import { ref, computed } from 'vue';
import { Head, Link, useForm, usePage } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Icon } from '@iconify/vue';

const props = defineProps({
    courses: {
        type: Array,
        default: () => []
    },
    selectedCourse: {
        type: Object,
        default: () => null
    },
    error: {
        type: String,
        default: ''
    }
});

const page = usePage();
const activeTab = ref('contenido');

// Form for Task Submissions
const showSubmitModal = ref(false);
const activeTask = ref(null);
const existingFileUrl = ref(null);
const submitForm = useForm({
    submission_text: '',
    file: null,
    delete_file: false
});

const openSubmitModal = (task, submission = null) => {
    activeTask.value = task;
    submitForm.submission_text = submission ? submission.submission_text : '';
    submitForm.file = null;
    submitForm.delete_file = false;
    existingFileUrl.value = (submission && submission.submission_file) ? submission.submission_file : null;
    showSubmitModal.value = true;
};

const handleFileChange = (e) => {
    submitForm.file = e.target.files[0];
    if (submitForm.file) {
        submitForm.delete_file = false;
    }
};

const sendTaskSubmission = () => {
    submitForm.post(route('student.independent-courses.task.submit', activeTask.value.id), {
        onSuccess: () => {
            showSubmitModal.value = false;
            submitForm.reset();
        }
    });
};

const deleteAttachment = () => {
    if (confirm('¿Estás seguro de que deseas eliminar el archivo adjunto entregado?')) {
        submitForm.delete_file = true;
        submitForm.file = null;
        submitForm.post(route('student.independent-courses.task.submit', activeTask.value.id), {
            preserveScroll: true,
            onSuccess: () => {
                existingFileUrl.value = null;
                submitForm.delete_file = false;
                alert('El archivo adjunto ha sido eliminado correctamente.');
            }
        });
    }
};

// Forum replies form
const activeForum = ref(null);
const forumForm = useForm({
    id_forum: '',
    id_parent: null,
    message: ''
});

const selectForum = (forum) => {
    activeForum.value = forum;
    forumForm.id_forum = forum.id;
    forumForm.id_parent = null;
    forumForm.message = '';
};

const submitForumMessage = () => {
    if (!forumForm.message.trim()) return;
    forumForm.post(route('independent-courses.forum.message.store'), {
        preserveScroll: true,
        onSuccess: () => {
            forumForm.message = '';
        }
    });
};

// Helper to find task grade/submission
const getTaskGrade = (taskId) => {
    if (!props.selectedCourse || !props.selectedCourse.grades) return null;
    return props.selectedCourse.grades.find(g => g.id_task === taskId);
};

// Helper for attendance colors
const getAttendanceBadgeClass = (status) => {
    switch (status) {
        case 'puntual':
            return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/50';
        case 'falta':
            return 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-100 dark:border-rose-900/50';
        case 'falta_justificada':
            return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-100 dark:border-amber-900/50';
        case 'tardanza':
            return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50';
        case 'tardanza_justificada':
            return 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400 border border-sky-100 dark:border-sky-900/50';
        default:
            return 'bg-slate-50 text-slate-700 dark:bg-slate-850 dark:text-slate-400 border border-slate-100 dark:border-slate-800';
    }
};

const getAttendanceLabel = (status) => {
    switch (status) {
        case 'puntual': return 'Puntual';
        case 'falta': return 'Falta';
        case 'falta_justificada': return 'Falta Justificada';
        case 'tardanza': return 'Tardanza';
        case 'tardanza_justificada': return 'Tardanza Justificada';
        default: return status;
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) {
        return dateStr;
    }
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-PE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return dateStr;
    }
};

const parseResources = (resourcesStr) => {
    if (!resourcesStr) return [];
    if (Array.isArray(resourcesStr)) return resourcesStr;
    try {
        return JSON.parse(resourcesStr);
    } catch (e) {
        return [];
    }
};

// Filtered forum messages for active forum
const activeForumMessages = computed(() => {
    if (!activeForum.value || !props.selectedCourse.forumMessages) return [];
    return props.selectedCourse.forumMessages.filter(m => m.id_forum === activeForum.value.id);
});
</script>

<template>
    <Head :title="selectedCourse ? `Curso: ${selectedCourse.info.label}` : 'Mis Cursos de Formación'" />

    <BreezeAuthenticatedLayout>
        <div>
            
            <!-- Error State -->
            <div v-if="error" class="p-6 bg-rose-50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/50 rounded-2xl text-center">
                <Icon icon="tabler:alert-triangle" class="w-12 h-12 text-rose-500 mx-auto" />
                <h3 class="text-sm font-semibold text-rose-800 dark:text-rose-400 mt-2">{{ error }}</h3>
                <p class="text-xs text-rose-600 dark:text-rose-500 mt-1">Por favor consulte con el área administrativa.</p>
            </div>

            <template v-else>
                <!-- LIST VIEW: NO COURSE SELECTED -->
                <div v-if="!selectedCourse" class="space-y-6">
                    <div class="flex flex-col gap-2">
                        <span class="text-xs text-indigo-600 dark:text-indigo-400 font-bold uppercase tracking-wider">Estudiante</span>
                        <h1 class="text-2xl md:text-3xl font-extrabold text-slate-800 dark:text-white">Mis Cursos de Formación / Libres</h1>
                        <p class="text-xs text-slate-500 dark:text-slate-400">Accede a tus materiales de clase, rinde tus tareas, participa de los foros y monitorea tus calificaciones.</p>
                    </div>

                    <!-- Enrolled Courses Grid -->
                    <div v-if="courses.length === 0" class="text-center py-16 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm">
                        <Icon icon="tabler:school-off" class="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto animate-pulse" />
                        <h3 class="text-base font-bold text-slate-700 dark:text-slate-300 mt-4">Aún no estás matriculado en ningún curso</h3>
                        <p class="text-xs text-slate-400 mt-1 max-w-sm mx-auto">Comunícate con la coordinación académica para registrarte en los cursos libres correspondientes al ciclo.</p>
                    </div>

                    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="course in courses" :key="course.id" 
                             class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col group hover:-translate-y-1">
                            <div class="h-2 bg-gradient-to-r from-indigo-500 to-violet-600"></div>
                            <div class="p-6 flex-1 flex flex-col justify-between space-y-4">
                                <div class="space-y-2">
                                    <div class="flex items-center justify-between">
                                        <span class="text-[9px] font-bold font-mono px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded uppercase">
                                            {{ course.ref || 'CURSO' }}
                                        </span>
                                        <span class="text-xs text-slate-400 dark:text-slate-500 font-semibold flex items-center gap-1">
                                            <Icon icon="tabler:calendar" class="w-3.5 h-3.5" />
                                            Año {{ course.anio }}
                                        </span>
                                    </div>
                                    <h3 class="text-base font-bold text-slate-800 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                        {{ course.label }}
                                    </h3>
                                    <p class="text-xs text-slate-400 dark:text-slate-500 line-clamp-2">{{ course.description || 'Sin descripción disponible.' }}</p>
                                </div>

                                <div class="pt-4 border-t border-slate-50 dark:border-slate-800 flex items-center justify-between">
                                    <span v-if="course.credits" class="text-xs text-slate-500 dark:text-slate-400 font-medium flex items-center gap-1">
                                        <Icon icon="tabler:award" class="w-4 h-4 text-indigo-500" />
                                        {{ course.credits }} Créditos
                                    </span>
                                    <span v-else></span>
                                    
                                    <Link :href="route('student.independent-courses.show', course.id)"
                                          class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-600 text-indigo-600 hover:text-white dark:bg-indigo-950/40 dark:text-indigo-400 dark:hover:bg-indigo-600 dark:hover:text-white rounded-xl text-xs font-bold transition-all">
                                        Ingresar
                                        <Icon icon="tabler:arrow-right" class="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- DETAIL VIEW: COURSE DETAILS -->
                <div v-else class="space-y-6">
                    
                    <!-- Navigation Header Card -->
                    <div class="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 transition-all duration-300">
                        <div class="space-y-2">
                            <div class="flex items-center gap-3">
                                <Link :href="route('student.independent-courses')" 
                                      class="p-2 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700/80 text-slate-500 dark:text-slate-400 rounded-xl transition-all">
                                    <Icon icon="tabler:arrow-left" class="w-5 h-5" />
                                </Link>
                                <div>
                                    <span class="text-[10px] font-bold font-mono px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 rounded uppercase">
                                        {{ selectedCourse.info.ref || 'CURSO' }}
                                    </span>
                                    <h1 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-white mt-1">{{ selectedCourse.info.label }}</h1>
                                </div>
                            </div>
                            <div class="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 font-medium pl-12">
                                <span class="flex items-center gap-1">
                                    <Icon icon="tabler:calendar" class="w-4 h-4 text-slate-400" />
                                    Año: {{ selectedCourse.info.anio }}
                                </span>
                                <span v-if="selectedCourse.info.credits" class="flex items-center gap-1">
                                    <Icon icon="tabler:award" class="w-4 h-4 text-slate-400" />
                                    Créditos: {{ selectedCourse.info.credits }}
                                </span>
                            </div>
                        </div>

                        <!-- Tab Menu -->
                        <div class="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
                            <div class="flex items-center gap-1.5 p-1.5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl border border-slate-100 dark:border-slate-800/80 w-max">
                                <button @click="activeTab = 'contenido'"
                                        :class="[activeTab === 'contenido' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                        class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                    <Icon icon="tabler:book" class="w-4 h-4" />
                                    Contenido del curso
                                </button>
                                <button @click="activeTab = 'asistencia'"
                                        :class="[activeTab === 'asistencia' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                        class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                    <Icon icon="tabler:clipboard-check" class="w-4 h-4" />
                                    Asistencia
                                </button>
                                <button @click="activeTab = 'foros'"
                                        :class="[activeTab === 'foros' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                        class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                    <Icon icon="tabler:messages" class="w-4 h-4" />
                                    Foros
                                </button>
                                <button @click="activeTab = 'calificaciones'"
                                        :class="[activeTab === 'calificaciones' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                        class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                    <Icon icon="tabler:award" class="w-4 h-4" />
                                    Calificaciones
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- TAB CONTENTS -->
                    <div class="space-y-6">

                        <!-- 1. CONTENIDO DEL CURSO (Roadmap/Timeline) -->
                        <div v-if="activeTab === 'contenido'" class="space-y-6">
                            <h2 class="text-lg font-bold text-slate-800 dark:text-white">Curricula & Ruta de Aprendizaje</h2>

                            <div v-if="selectedCourse.topics.length === 0" class="text-center py-16 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm">
                                <Icon icon="tabler:books-off" class="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto" />
                                <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-4">Aún no se ha estructurado la curricula</h4>
                                <p class="text-xs text-slate-400 mt-1">Vuelve pronto para revisar los contenidos del curso.</p>
                            </div>

                            <div v-else class="relative border-l-2 border-slate-100 dark:border-slate-800 ml-4 md:ml-8 space-y-8 py-2">
                                <div v-for="topic in selectedCourse.topics" :key="topic.id" class="relative pl-6 md:pl-10">
                                    
                                    <!-- Timeline Bullet -->
                                    <div class="absolute -left-[11px] top-1 w-5 h-5 rounded-full border-2 border-white dark:border-slate-900 flex items-center justify-center bg-indigo-600 shadow-md">
                                        <span class="text-[9px] font-bold text-white">{{ topic.week_number }}</span>
                                    </div>

                                    <!-- Topic Card -->
                                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm rounded-2xl p-6 transition-all hover:shadow-md">
                                        <div class="space-y-1">
                                            <div class="flex flex-wrap items-center gap-2">
                                                <span class="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded">
                                                    Semana {{ topic.week_number }}
                                                </span>
                                                <span v-if="topic.start_date || topic.end_date" class="text-[10px] text-slate-400 font-medium">
                                                    ({{ formatDate(topic.start_date) }} - {{ formatDate(topic.end_date) }})
                                                </span>
                                            </div>
                                            <h3 class="text-base font-bold text-slate-800 dark:text-white">{{ topic.title }}</h3>
                                            <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{{ topic.description }}</p>
                                        </div>

                                        <!-- Resources & Tasks Grid -->
                                        <div class="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 pt-6 border-t border-slate-50 dark:border-slate-800/60">
                                            
                                            <!-- Study Resources -->
                                            <div class="space-y-3">
                                                <h4 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                                    <Icon icon="tabler:folders" class="w-4 h-4 text-slate-400" />
                                                    Recursos de Estudio
                                                </h4>
                                                <ul v-if="parseResources(topic.resources).length" class="space-y-2">
                                                    <li v-for="(res, ri) in parseResources(topic.resources)" :key="ri" 
                                                        class="p-2.5 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/40 dark:hover:bg-slate-800/80 rounded-xl transition-all flex items-center justify-between gap-3 text-xs">
                                                        <div class="flex items-center gap-2 overflow-hidden">
                                                            <Icon :icon="res.type === 'link' ? 'tabler:link' : 'tabler:file-text'" 
                                                                  :class="res.type === 'link' ? 'text-indigo-500' : 'text-rose-500'" 
                                                                  class="w-4 h-4 shrink-0" />
                                                            <span class="font-medium text-slate-700 dark:text-slate-300 truncate" :title="res.name">{{ res.name }}</span>
                                                        </div>

                                                        <a :href="res.value" target="_blank"
                                                           class="inline-flex items-center gap-1 px-2.5 py-1 bg-white hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-600 text-[10px] text-slate-600 dark:text-slate-300 rounded-lg border border-slate-150 dark:border-slate-650 font-bold transition-all shadow-sm">
                                                            <Icon :icon="res.type === 'link' ? 'tabler:external-link' : 'tabler:download'" class="w-3.5 h-3.5" />
                                                            {{ res.type === 'link' ? 'Abrir' : 'Descargar' }}
                                                        </a>
                                                    </li>
                                                </ul>
                                                <div v-else class="text-center py-4 bg-slate-50/40 dark:bg-slate-950/20 rounded-xl text-slate-400 text-xs">
                                                    No hay recursos subidos para esta semana.
                                                </div>
                                            </div>

                                            <!-- Tasks / Homework / Exams -->
                                            <div class="space-y-3">
                                                <h4 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                                    <Icon icon="tabler:clipboard-list" class="w-4 h-4 text-slate-400" />
                                                    Evaluaciones / Tareas
                                                </h4>
                                                
                                                <div v-if="topic.tasks && topic.tasks.length" class="space-y-3">
                                                    <div v-for="task in topic.tasks" :key="task.id" 
                                                         class="p-4 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-100 dark:border-slate-800/60 space-y-3">
                                                        <div class="flex items-start justify-between gap-4">
                                                            <div>
                                                                <span :class="task.type === 'examen' ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400' : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400'"
                                                                      class="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded tracking-wider">
                                                                    {{ task.type }}
                                                                </span>
                                                                <h5 class="text-xs font-bold text-slate-800 dark:text-white mt-1.5">{{ task.title }}</h5>
                                                                <p class="text-[11px] text-slate-400 mt-1 leading-relaxed">{{ task.description }}</p>
                                                            </div>
                                                            <div class="text-right shrink-0">
                                                                <span class="text-xs font-extrabold text-slate-700 dark:text-slate-300 block">
                                                                    {{ task.max_points }} Pts
                                                                </span>
                                                                <span v-if="task.due_date" class="text-[10px] text-slate-400 font-medium block mt-0.5">
                                                                    Límite: {{ formatDate(task.due_date) }}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <!-- Task Submission Status and Action -->
                                                        <div class="pt-3 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-between text-xs">
                                                            <div class="flex items-center gap-1.5">
                                                                <template v-if="getTaskGrade(task.id)">
                                                                    <span v-if="getTaskGrade(task.id).status === 'graded'" class="flex items-center gap-1 text-emerald-600 font-semibold">
                                                                        <Icon icon="tabler:circle-check" class="w-4 h-4" />
                                                                        Calificado: {{ getTaskGrade(task.id).grade }}
                                                                    </span>
                                                                    <span v-else class="flex items-center gap-1 text-amber-500 font-semibold">
                                                                        <Icon icon="tabler:clock" class="w-4 h-4" />
                                                                        Entregado (Pendiente)
                                                                    </span>
                                                                </template>
                                                                <template v-else>
                                                                    <span class="flex items-center gap-1 text-slate-400">
                                                                        <Icon icon="tabler:circle" class="w-4 h-4" />
                                                                        Sin entregar
                                                                    </span>
                                                                </template>
                                                            </div>

                                                            <!-- Submission Button -->
                                                            <button v-if="!getTaskGrade(task.id) || getTaskGrade(task.id).status !== 'graded'"
                                                                    @click="openSubmitModal(task, getTaskGrade(task.id))"
                                                                    class="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold text-[10px] transition-all flex items-center gap-1 shadow-sm">
                                                                <Icon icon="tabler:upload" class="w-3.5 h-3.5" />
                                                                {{ getTaskGrade(task.id) ? 'Modificar' : 'Responder' }}
                                                            </button>
                                                            
                                                            <!-- Submitted content details if graded -->
                                                            <div v-else class="text-[10px] text-slate-400">
                                                                No se permiten más entregas.
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-else class="text-center py-4 bg-slate-50/40 dark:bg-slate-950/20 rounded-xl text-slate-400 text-xs">
                                                    No hay evaluaciones registradas para esta semana.
                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        <!-- 2. ASISTENCIA -->
                        <div v-if="activeTab === 'asistencia'" class="space-y-6">
                            <h2 class="text-lg font-bold text-slate-800 dark:text-white">Mi Registro de Asistencia</h2>

                            <div v-if="selectedCourse.attendance.length === 0" class="text-center py-16 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm">
                                <Icon icon="tabler:calendar-cancel" class="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto" />
                                <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-4">Aún no se ha registrado asistencia en este curso</h4>
                                <p class="text-xs text-slate-400 mt-1">El docente o coordinador marcará tu asistencia semanal en clase.</p>
                            </div>

                            <div v-else class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm rounded-3xl overflow-hidden">
                                <div class="overflow-x-auto">
                                    <table class="w-full text-left border-collapse">
                                        <thead>
                                            <tr class="bg-slate-50 dark:bg-slate-850/50 border-b border-slate-100 dark:border-slate-800">
                                                <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Fecha</th>
                                                <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Estado</th>
                                                <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Observación / Nota</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-slate-50 dark:divide-slate-800/80">
                                            <tr v-for="att in selectedCourse.attendance" :key="att.id" class="hover:bg-slate-50/50 dark:hover:bg-slate-850/30 transition-colors">
                                                <td class="px-6 py-4 text-sm font-semibold text-slate-800 dark:text-slate-200">
                                                    {{ formatDate(att.date) }}
                                                </td>
                                                <td class="px-6 py-4">
                                                    <span :class="getAttendanceBadgeClass(att.status)"
                                                          class="inline-flex px-2.5 py-1 text-[11px] font-bold rounded-xl leading-none">
                                                        {{ getAttendanceLabel(att.status) }}
                                                    </span>
                                                </td>
                                                <td class="px-6 py-4 text-xs text-slate-500 dark:text-slate-400">
                                                    {{ att.observation || '-' }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <!-- 3. FOROS -->
                        <div v-if="activeTab === 'foros'" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            
                            <!-- Forums List Sidebar -->
                            <div class="lg:col-span-1 space-y-4">
                                <h3 class="text-sm font-bold text-slate-850 dark:text-slate-200 uppercase tracking-wider">Foros Disponibles</h3>
                                
                                <div v-if="selectedCourse.forums.length === 0" class="text-center py-8 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl text-slate-400 text-xs">
                                    No hay foros de discusión creados para este curso.
                                </div>

                                <div v-else class="space-y-3">
                                    <button v-for="forum in selectedCourse.forums" :key="forum.id"
                                            @click="selectForum(forum)"
                                            :class="activeForum && activeForum.id === forum.id ? 'border-indigo-500 bg-indigo-50/20 dark:bg-indigo-950/20' : 'border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-200 dark:hover:border-slate-700'"
                                            class="w-full text-left border rounded-2xl p-4 transition-all duration-200 flex flex-col gap-2">
                                        <div class="flex items-center justify-between gap-2 w-full">
                                            <span class="text-[10px] text-slate-400 font-medium">Vence: {{ formatDate(forum.end_date) }}</span>
                                            <span class="text-[10px] px-2 py-0.5 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-full font-bold flex items-center gap-0.5">
                                                <Icon icon="tabler:message" class="w-3 h-3" />
                                                {{ forum.messages_count || 0 }}
                                            </span>
                                        </div>
                                        <h4 class="text-xs font-bold text-slate-800 dark:text-white line-clamp-1">{{ forum.title }}</h4>
                                        <p class="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">{{ forum.description }}</p>
                                    </button>
                                </div>
                            </div>

                            <!-- Forums Detail Area / Active Thread -->
                            <div class="lg:col-span-2 space-y-4">
                                <div v-if="!activeForum" class="h-64 flex flex-col items-center justify-center text-center bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm p-6 text-slate-400 text-xs">
                                    <Icon icon="tabler:message-2" class="w-12 h-12 text-slate-350 dark:text-slate-650 mb-2 animate-bounce" />
                                    Selecciona un foro de debate de la lista para leer e interactuar.
                                </div>

                                <div v-else class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm p-6 space-y-6">
                                    <!-- Forum Header Info -->
                                    <div class="pb-6 border-b border-slate-50 dark:border-slate-800 flex flex-col gap-2">
                                        <div class="flex items-center justify-between">
                                            <h3 class="text-base font-extrabold text-slate-800 dark:text-white">{{ activeForum.title }}</h3>
                                            <span class="text-[10px] font-bold px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-full">
                                                Vigencia: {{ formatDate(activeForum.start_date) }} al {{ formatDate(activeForum.end_date) }}
                                            </span>
                                        </div>
                                        <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{{ activeForum.description }}</p>
                                    </div>

                                    <!-- Comments List -->
                                    <div class="space-y-4 max-h-[380px] overflow-y-auto pr-2 custom-scrollbar">
                                        <div v-if="activeForumMessages.length === 0" class="text-center py-10 text-xs text-slate-400">
                                            Nadie ha comentado todavía. ¡Sé el primero en iniciar la conversación!
                                        </div>

                                        <div v-else v-for="msg in activeForumMessages" :key="msg.id" 
                                             class="p-4 bg-slate-50 dark:bg-slate-850/40 rounded-2xl border border-slate-100/50 dark:border-slate-800/40 flex flex-col gap-2">
                                            <div class="flex items-center justify-between">
                                                <div class="flex items-center gap-2">
                                                    <div class="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 flex items-center justify-center font-bold text-[10px] uppercase">
                                                        {{ msg.user ? msg.user.name.substring(0, 2) : 'US' }}
                                                    </div>
                                                    <div>
                                                        <h5 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">{{ msg.user ? msg.user.name : 'Usuario' }}</h5>
                                                        <span class="text-[9px] text-slate-400 font-medium">{{ formatDateTime(msg.created_at) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <p class="text-xs text-slate-600 dark:text-slate-400 leading-relaxed pl-9 whitespace-pre-line">{{ msg.message }}</p>

                                            <!-- Replies inside thread -->
                                            <div v-if="msg.replies && msg.replies.length" class="pl-9 space-y-3 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800/50">
                                                <div v-for="reply in msg.replies" :key="reply.id" class="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-50 dark:border-slate-850 flex flex-col gap-1">
                                                    <div class="flex items-center justify-between">
                                                        <div class="flex items-center gap-1.5">
                                                            <div class="w-5 h-5 rounded-full bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-400 flex items-center justify-center font-bold text-[8px] uppercase">
                                                                {{ reply.user ? reply.user.name.substring(0, 2) : 'US' }}
                                                            </div>
                                                            <span class="text-[10px] font-bold text-slate-600 dark:text-slate-300 uppercase">{{ reply.user ? reply.user.name : 'Usuario' }}</span>
                                                        </div>
                                                        <span class="text-[8px] text-slate-400 font-medium">{{ formatDateTime(reply.created_at) }}</span>
                                                    </div>
                                                    <p class="text-xs text-slate-500 dark:text-slate-400 pl-6 leading-relaxed">{{ reply.message }}</p>
                                                </div>
                                            </div>

                                            <!-- Reply button (direct response helper) -->
                                            <div class="pl-9 mt-1 flex justify-end">
                                                <button @click="forumForm.id_parent = msg.id" 
                                                        class="text-[10px] text-indigo-600 dark:text-indigo-400 font-bold hover:underline inline-flex items-center gap-0.5">
                                                    <Icon icon="tabler:corner-down-right" class="w-3.5 h-3.5" />
                                                    Responder Comentario
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Add Comment Box -->
                                    <div class="pt-4 border-t border-slate-50 dark:border-slate-800 space-y-3">
                                        <div v-if="forumForm.id_parent" class="px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center justify-between text-[10px] text-slate-500">
                                            <span>Respondiendo al comentario ID #{{ forumForm.id_parent }}</span>
                                            <button @click="forumForm.id_parent = null" class="text-rose-500 font-bold hover:underline">Cancelar</button>
                                        </div>

                                        <form @submit.prevent="submitForumMessage" class="flex gap-2">
                                            <input v-model="forumForm.message" type="text" 
                                                   placeholder="Escribe tu aporte en el foro de debate..." 
                                                   class="flex-1 px-4 py-2 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 focus:border-indigo-500 dark:focus:border-indigo-500 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:text-white" />
                                            <button type="submit" :disabled="forumForm.processing"
                                                    class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs shadow-sm shadow-indigo-100 dark:shadow-none transition-all flex items-center gap-1">
                                                <Icon icon="tabler:send" class="w-4 h-4" />
                                                Enviar
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 4. CALIFICACIONES -->
                        <div v-if="activeTab === 'calificaciones'" class="space-y-6">
                            <h2 class="text-lg font-bold text-slate-800 dark:text-white">Mis Calificaciones y Retroalimentación</h2>

                            <div v-if="selectedCourse.topics.every(t => !t.tasks || t.tasks.length === 0)" class="text-center py-16 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm">
                                <Icon icon="tabler:award-off" class="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto" />
                                <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-4">Aún no se han configurado evaluaciones</h4>
                                <p class="text-xs text-slate-400 mt-1">Las evaluaciones parciales se mostrarán aquí con su puntaje respectivo.</p>
                            </div>

                            <div v-else class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm rounded-3xl overflow-hidden">
                                <div class="overflow-x-auto">
                                    <table class="w-full text-left border-collapse">
                                        <thead>
                                            <tr class="bg-slate-50 dark:bg-slate-850/50 border-b border-slate-100 dark:border-slate-800">
                                                <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Evaluación / Tarea</th>
                                                <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Nota Lograda</th>
                                                <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Fecha de Entrega</th>
                                                <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Retroalimentación del Docente</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-slate-50 dark:divide-slate-800/80">
                                            <template v-for="topic in selectedCourse.topics" :key="topic.id">
                                                <tr v-for="task in topic.tasks" :key="task.id" class="hover:bg-slate-50/50 dark:hover:bg-slate-850/30 transition-colors">
                                                    <td class="px-6 py-4 text-sm">
                                                        <div class="font-bold text-slate-800 dark:text-slate-200">{{ task.title }}</div>
                                                        <div class="text-[10px] text-slate-400 mt-0.5">Semana {{ topic.week_number }} | Tipo: {{ task.type }}</div>
                                                        
                                                        <!-- Evaluation Criteria Breakdown -->
                                                        <div v-if="task.evaluation_criteria && task.evaluation_criteria.length > 0" class="mt-2.5 flex flex-wrap gap-1.5">
                                                            <div v-for="crit in task.evaluation_criteria" :key="crit.id"
                                                                 class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] bg-slate-50 dark:bg-slate-800/80 border border-slate-200/70 dark:border-slate-750 font-medium">
                                                                <span class="text-slate-600 dark:text-slate-300 font-semibold">{{ crit.name }}:</span>
                                                                <span v-if="getTaskGrade(task.id) && getTaskGrade(task.id).criteria_grades && getTaskGrade(task.id).criteria_grades[crit.id] !== undefined && getTaskGrade(task.id).criteria_grades[crit.id] !== ''"
                                                                      class="font-bold text-indigo-600 dark:text-indigo-400">
                                                                    {{ getTaskGrade(task.id).criteria_grades[crit.id] }}
                                                                    <span class="text-slate-400 font-normal text-[9px]">/ {{ crit.max_score }}</span>
                                                                </span>
                                                                <span v-else class="text-slate-400 text-[10px] italic">
                                                                    - / {{ crit.max_score }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td class="px-6 py-4 text-sm font-semibold">
                                                        <span v-if="getTaskGrade(task.id) && getTaskGrade(task.id).status === 'graded'"
                                                              :class="getTaskGrade(task.id).grade >= 11 ? 'text-indigo-600 dark:text-indigo-400' : 'text-rose-500'" 
                                                              class="text-base font-extrabold">
                                                            {{ getTaskGrade(task.id).grade }}
                                                        </span>
                                                        <span v-else-if="getTaskGrade(task.id) && getTaskGrade(task.id).status !== 'graded'" class="text-slate-400 text-xs italic">
                                                            Entregado (Pdte.)
                                                        </span>
                                                        <span v-else class="text-slate-350 dark:text-slate-650 text-xs italic">
                                                            Sin entregar
                                                        </span>
                                                        <span class="text-xs text-slate-400 font-medium ml-1">/ {{ task.max_points }}</span>
                                                    </td>
                                                    <td class="px-6 py-4 text-xs text-slate-500 dark:text-slate-400">
                                                        {{ getTaskGrade(task.id) ? formatDateTime(getTaskGrade(task.id).submitted_at) : '-' }}
                                                    </td>
                                                    <td class="px-6 py-4 text-xs text-slate-500 dark:text-slate-400 italic">
                                                        {{ (getTaskGrade(task.id) && getTaskGrade(task.id).feedback) ? getTaskGrade(task.id).feedback : 'Sin observaciones.' }}
                                                    </td>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                    </div>

                </div>
            </template>

        </div>
    </BreezeAuthenticatedLayout>

    <!-- TASK SUBMISSION DIALOG MODAL -->
    <div v-if="showSubmitModal" class="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl max-w-lg w-full p-6 space-y-6 shadow-2xl relative">
            <button @click="showSubmitModal = false" class="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <Icon icon="tabler:x" class="w-5 h-5" />
            </button>

            <div class="space-y-1">
                <span class="text-[9px] font-extrabold uppercase px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded">
                    Entregar Respuesta
                </span>
                <h3 class="text-base font-bold text-slate-800 dark:text-white mt-1">{{ activeTask.title }}</h3>
                <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{{ activeTask.description }}</p>
            </div>

            <form @submit.prevent="sendTaskSubmission" class="space-y-4">
                <!-- Text Answer -->
                <div class="space-y-1">
                    <label class="text-xs font-semibold text-slate-700 dark:text-slate-300">Respuesta escrita (Opcional)</label>
                    <textarea v-model="submitForm.submission_text" 
                              rows="4"
                              placeholder="Describe tu respuesta o añade detalles de tu entrega aquí..."
                              class="w-full px-4 py-3 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800/80 rounded-xl focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:text-white"></textarea>
                </div>

                <!-- File Upload -->
                <div class="space-y-2">
                    <label class="text-xs font-semibold text-slate-700 dark:text-slate-300 block">Archivo adjunto (PDF, ZIP, DOC - Max 5MB)</label>
                    
                    <div v-if="existingFileUrl" class="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-950/40 border border-slate-100 dark:border-slate-800 rounded-2xl">
                        <a :href="existingFileUrl" target="_blank" class="flex items-center gap-2 text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-bold truncate max-w-[70%]">
                            <Icon icon="tabler:file-check" class="w-4 h-4 text-emerald-500 shrink-0" />
                            <span class="truncate">Ver archivo entregado</span>
                        </a>
                        <button type="button" @click="deleteAttachment"
                                class="flex items-center gap-1 px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/45 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-400 text-[10px] font-bold rounded-xl transition-all">
                            <Icon icon="tabler:trash" class="w-3.5 h-3.5" />
                            Eliminar
                        </button>
                    </div>

                    <div class="space-y-1">
                        <span v-if="existingFileUrl" class="text-[10px] text-slate-400 dark:text-slate-500 font-medium block">
                            Subir un archivo nuevo reemplazará el archivo actual.
                        </span>
                        <input type="file" @change="handleFileChange"
                               class="text-xs text-slate-500 dark:text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 dark:file:bg-indigo-950/40 file:text-indigo-600 dark:file:text-indigo-400 hover:file:bg-indigo-100 transition-all w-full" />
                    </div>
                </div>

                <div class="pt-4 border-t border-slate-50 dark:border-slate-800/60 flex items-center justify-end gap-3">
                    <button type="button" @click="showSubmitModal = false"
                            class="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-350 rounded-xl text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        Cancelar
                    </button>
                    <button type="submit" :disabled="submitForm.processing"
                            class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm shadow-indigo-100 dark:shadow-none transition-colors flex items-center gap-1.5">
                        <Icon icon="tabler:send" class="w-4 h-4" />
                        Enviar Entrega
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<style scoped>
/* Custom scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.3);
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.5);
}
</style>
